import { el, getApiBase, setStorage } from './utils';
import type { EmbedPoll, VoteMap } from './utils';

/**
 * Render the vote submission form (name, optional email, submit button).
 * Returns the form element.
 */
export function renderVoteForm(
	poll: EmbedPoll,
	token: string,
	votes: VoteMap,
	onSuccess: () => void
): HTMLElement {
	const settings = poll.settings || {};
	const form = el('div', { class: 'dte-form' });

	// Name input
	const nameGroup = el('div', { class: 'dte-input-group' });
	const nameLabel = el('label', { class: 'dte-input-label', for: `dte-name-${token}` }, ['Your name']);
	const nameInput = el('input', {
		class: 'dte-input',
		type: 'text',
		id: `dte-name-${token}`,
		placeholder: 'Enter your name',
		required: 'true'
	}) as HTMLInputElement;
	nameGroup.appendChild(nameLabel);
	nameGroup.appendChild(nameInput);
	form.appendChild(nameGroup);

	// Email input (only if required)
	let emailInput: HTMLInputElement | null = null;
	if (settings.require_email) {
		const emailGroup = el('div', { class: 'dte-input-group' });
		const emailLabel = el('label', { class: 'dte-input-label', for: `dte-email-${token}` }, ['Email']);
		emailInput = el('input', {
			class: 'dte-input',
			type: 'email',
			id: `dte-email-${token}`,
			placeholder: 'your@email.com',
			required: 'true'
		}) as HTMLInputElement;
		emailGroup.appendChild(emailLabel);
		emailGroup.appendChild(emailInput);
		form.appendChild(emailGroup);
	}

	// Error display
	const errorEl = el('div', { class: 'dte-error', style: 'display:none' });
	form.appendChild(errorEl);

	// Submit button
	const submitBtn = el('button', {
		class: 'dte-submit',
		type: 'button'
	}, ['Save My Votes']) as HTMLButtonElement;
	form.appendChild(submitBtn);

	// Submit handler
	submitBtn.addEventListener('click', async () => {
		const name = nameInput.value.trim();
		if (!name) {
			showError(errorEl, 'Please enter your name');
			nameInput.focus();
			return;
		}

		if (settings.require_email && emailInput && !emailInput.value.trim()) {
			showError(errorEl, 'Please enter your email');
			emailInput.focus();
			return;
		}

		// Build vote payload — only include slots with a response
		const votePayload = Object.entries(votes)
			.filter(([, response]) => response && response !== 'no')
			.map(([slot_id, response]) => ({ slot_id, response }));

		if (votePayload.length === 0) {
			showError(errorEl, 'Please select at least one option');
			return;
		}

		// Disable form during submission
		submitBtn.disabled = true;
		submitBtn.textContent = 'Saving...';
		hideError(errorEl);

		try {
			const apiBase = getApiBase();
			const res = await fetch(`${apiBase}/api/v1/embed/${token}/vote`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name,
					email: emailInput?.value.trim() || undefined,
					votes: votePayload
				})
			});

			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				const msg = data?.error?.message || `Error (${res.status})`;
				showError(errorEl, msg);
				return;
			}

			const data = await res.json();

			// Save to localStorage
			setStorage(token, {
				participant_token: data.participant_token,
				voted: true
			});

			// Trigger re-render
			onSuccess();
		} catch {
			showError(errorEl, 'Could not submit vote. Check your connection.');
		} finally {
			submitBtn.disabled = false;
			submitBtn.textContent = 'Save My Votes';
		}
	});

	return form;
}

function showError(el: HTMLElement, msg: string) {
	el.textContent = msg;
	el.style.display = 'block';
}

function hideError(el: HTMLElement) {
	el.style.display = 'none';
}
